/**
 * Admin theme: desktop-first, neutral slate surfaces with a calm blue accent and dense data
 * components (tables, badges, inputs) suited to back-office work.
 */
import {
  ActionIcon,
  Anchor,
  Badge,
  Button,
  Card,
  createTheme,
  darken,
  defaultVariantColorsResolver,
  luminance,
  Menu,
  Modal,
  NavLink,
  Notification,
  Paper,
  parseThemeColor,
  rem,
  Table,
  toRgba,
  Tooltip,
  type MantineColorsTuple,
  type MantineTheme,
  type VariantColorsResolver,
} from '@mantine/core';

/** Cool neutral scale (Tailwind «slate»); replaces Mantine gray for text, borders and surfaces. */
const slate: MantineColorsTuple = [
  '#f8fafc',
  '#f1f5f9',
  '#e2e8f0',
  '#cbd5e1',
  '#94a3b8',
  '#64748b',
  '#475569',
  '#334155',
  '#1e293b',
  '#0f172a',
];

/** Primary blue: actions, active navigation, links. */
const brand: MantineColorsTuple = [
  '#eff6ff',
  '#dbeafe',
  '#bfdbfe',
  '#93c5fd',
  '#60a5fa',
  '#3b82f6',
  '#2563eb',
  '#1d4ed8',
  '#1e40af',
  '#1e3a8a',
];

/** WCAG 2.1 AA for normal text. */
const MIN_CONTRAST = 4.5;
/**
 * Text of the light and outline variants is checked against the light tint on the page grey with a
 * margin for a tint on a tint (a status badge inside an alert reads ~10 % darker).
 */
const MIN_CONTRAST_ON_TINT = 5;
/** Opacity of the light variant's tint (Mantine's `--mantine-color-*-light`). */
const LIGHT_TINT = 0.1;

function contrast(a: string, b: string): number {
  const [lighter, darker] = [luminance(a), luminance(b)].sort((x, y) => y - x);
  return (lighter + 0.05) / (darker + 0.05);
}

/** `color` at `opacity` over the opaque `background`. */
function blend(color: string, opacity: number, background: string): string {
  const top = toRgba(color);
  const bottom = toRgba(background);
  const mix = (a: number, b: number) => Math.round(a * opacity + b * (1 - opacity));
  return `rgb(${mix(top.r, bottom.r)}, ${mix(top.g, bottom.g)}, ${mix(top.b, bottom.b)})`;
}

/**
 * The first shade from the 6th on that reads at the given contrast; past the 9th — the 9th
 * darkened step by step (green, orange and yellow never get there within the palette).
 */
function readableShade(shades: MantineColorsTuple, reads: (color: string) => boolean): string {
  for (const shade of shades.slice(6)) if (reads(shade)) return shade;
  for (let step = 1; step < 20; step += 1) {
    const darker = darken(shades[9], step * 0.05);
    if (reads(darker)) return darker;
  }
  return '#000000';
}

const readableColors = new Map<string, string>();

/** Memoised `readableShade` of a palette colour for one use (`kind`). */
function readable(theme: MantineTheme, color: string, kind: 'filled' | 'tint' | 'plain'): string {
  const key = `${kind}:${color}`;
  let value = readableColors.get(key);
  if (value === undefined) {
    const shades = theme.colors[color];
    const page = theme.colors.gray[0];
    const tint = blend(shades[6], LIGHT_TINT, page);
    value = readableShade(shades, (shade) => {
      if (kind === 'filled') return contrast(theme.white, shade) >= MIN_CONTRAST;
      if (kind === 'tint') return contrast(shade, tint) >= MIN_CONTRAST_ON_TINT;
      return contrast(shade, page) >= MIN_CONTRAST;
    });
    readableColors.set(key, value);
  }
  return value;
}

/**
 * Mantine paints variants with the 6th shade: white text on filled red reads at 3.3:1, on orange at
 * 2.6:1; light buttons, light badges and alert titles at 2–3:1. The palette stays as it is — a
 * variant whose 6th shade does not read takes a darker shade of the same colour (WCAG AA ≥ 4.5:1):
 * the background of `filled`, the text of `light`, `outline`, `subtle`, `transparent` and `white`.
 * Colours that already read (the brand blue, the greys) and explicit shades (`orange.8`) are kept.
 */
const variantColorResolver: VariantColorsResolver = (input) => {
  const colors = defaultVariantColorsResolver(input);
  const { theme, variant } = input;
  const parsed = parseThemeColor({ color: input.color ?? theme.primaryColor, theme });
  if (!parsed.isThemeColor || parsed.shade !== undefined) return colors;
  switch (variant) {
    case 'filled': {
      const background = readable(theme, parsed.color, 'filled');
      if (background === theme.colors[parsed.color][6]) return colors;
      return { ...colors, background, hover: darken(background, 0.1) };
    }
    case 'light':
      return { ...colors, color: readable(theme, parsed.color, 'tint') };
    // Outline badges often sit on a tinted surface (a flag card): the same margin as light text.
    case 'outline': {
      const color = readable(theme, parsed.color, 'tint');
      return { ...colors, color, border: `${rem(1)} solid ${color}` };
    }
    case 'subtle':
    case 'transparent':
    case 'white':
      return { ...colors, color: readable(theme, parsed.color, 'plain') };
    default:
      return colors;
  }
};

export const theme = createTheme({
  colors: { brand, slate, gray: slate },
  primaryColor: 'brand',
  primaryShade: 6,
  variantColorResolver,
  black: slate[9],
  white: '#ffffff',
  fontFamily:
    "Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
  fontFamilyMonospace: "'JetBrains Mono', ui-monospace, SFMono-Regular, Menlo, Consolas, monospace",
  headings: {
    fontWeight: '600',
    sizes: {
      h1: { fontSize: '1.75rem', lineHeight: '1.3' },
      h2: { fontSize: '1.375rem', lineHeight: '1.35' },
      h3: { fontSize: '1.125rem', lineHeight: '1.4' },
    },
  },
  defaultRadius: 'sm',
  cursorType: 'pointer',
  components: {
    ActionIcon: ActionIcon.extend({ defaultProps: { variant: 'subtle', color: 'gray' } }),
    Anchor: Anchor.extend({ defaultProps: { underline: 'hover' } }),
    Badge: Badge.extend({ defaultProps: { variant: 'light', radius: 'sm', tt: 'none', fw: 600 } }),
    Button: Button.extend({ defaultProps: { radius: 'sm' } }),
    Card: Card.extend({ defaultProps: { withBorder: true, radius: 'md', padding: 'lg' } }),
    Menu: Menu.extend({ defaultProps: { shadow: 'md', radius: 'md' } }),
    Modal: Modal.extend({ defaultProps: { radius: 'md', overlayProps: { backgroundOpacity: 0.45, blur: 2 } } }),
    NavLink: NavLink.extend({ defaultProps: { variant: 'light' } }),
    Notification: Notification.extend({ defaultProps: { radius: 'md', withBorder: true } }),
    Paper: Paper.extend({ defaultProps: { withBorder: true, radius: 'md' } }),
    Table: Table.extend({
      defaultProps: { verticalSpacing: 6, horizontalSpacing: 'sm', fz: 'sm', highlightOnHover: true },
    }),
    Tooltip: Tooltip.extend({ defaultProps: { withArrow: true, openDelay: 250 } }),
  },
});
